<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useMagicKeys } from "@vueuse/core";
import { t } from "i18next";
import { useNftStore } from "@/stores/integrations/useNftStore";
import type { Nft } from "@/shared/types/integrations/CoinGecko/Nft";

const { nfts } = useNftStore();

const inputValue = ref<string>("");
const searchResponse = ref<Nft[]>([]);

const searchValue = ref<Nft[]>(JSON.parse(localStorage.getItem("searchNftValue") || "[]"));

let debounceTimeout: NodeJS.Timeout;

function searchNfts(input: string): void {
	if (input.length === 0) return;

	const lowerInput = input.toLowerCase();
	searchResponse.value = nfts
		.filter(
			(item) =>
				item.name.toLowerCase().includes(lowerInput) ||
				item.symbol.toLowerCase().includes(lowerInput) ||
				item.asset_platform_id.toLowerCase().includes(lowerInput) ||
				item.contract_address.toLowerCase().includes(lowerInput),
		)
		.slice(0, 7);
}

function debounceSearch(input: string): void {
	inputValue.value = input;

	clearTimeout(debounceTimeout);
	debounceTimeout = setTimeout(() => {
		searchNfts(input);
	}, 300);
}

function saveToLocalStorage(nft: Nft): void {
	if (!searchValue.value.some((item) => item.id === nft.id)) {
		if (searchValue.value.length === 4) {
			searchValue.value.pop();
		}
		searchValue.value.unshift(nft);
	}
	localStorage.setItem("searchNftValue", JSON.stringify(searchValue.value));
	inputValue.value = "";
	blur();
}

const isFocused = ref<boolean>(false);

function focus() {
	inputValue.value = "";
	isFocused.value = true;
}
function blur() {
	isFocused.value = false;
}
function handleOpenChange() {
	isFocused.value = !isFocused.value;
}

const { Meta_K, Ctrl_K } = useMagicKeys({
	passive: false,
	onEventFired(e) {
		if (e.key === "k" && (e.metaKey || e.ctrlKey)) e.preventDefault();
	},
});

watch([Meta_K, Ctrl_K], (v) => {
	if (v[0] || v[1]) handleOpenChange();
});

onMounted(() => {
	blur();
});
</script>

<template>
    <Command class="group rounded-lg border shadow-md max-w-80 md:max-w-[450px] mx-0 z-50 relative duration-300">
        <div class="w-full" cmdk-input-wrapper>
            <CommandInput @click="focus" :icon-text="'dark'" :placeholder="t('Pesquisar...')" class="w-full"
                name="fake-search" @keydown.prevent aria-label="Pesquisar NFTs" aria-haspopup="dialog"
                :aria-expanded="isFocused" />
        </div>
        <kbd
            class="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium text-text opacity-100 absolute top-[10px] right-4 bg-transparent my-0">
            <span class="text-xs">⌘</span>K
        </kbd>
        <CommandDialog v-model:open="isFocused" :title="t('Pesquisar')">
            <div class="flex items-center border-b bg-darkAlt pl-4" cmdk-input-wrapper>
                <CommandInput :placeholder="t('Pesquisar...')" @input="debounceSearch($event.target.value)"
                    :icon-text="'light'" name="search" />
            </div>
            <CommandList>
                <CommandGroup :heading="t('Recentes')" v-if="searchValue.length > 0 && inputValue.length === 0">
                    <CommandItem @click="saveToLocalStorage(item)" v-for="item in searchValue" :key="item.id"
                        :to="`/nfts/${item.id}`" :value="`${item.name} recent`">
                        <span class="text-light">{{ item.name }}</span>
                        <span class="ml-1 text-dark text-xs">({{ item.symbol }})</span>
                    </CommandItem>
                </CommandGroup>
                <CommandSeparator />
                <CommandEmpty>
                    <span v-translate>Nenhum resultado encontrado.</span>
                </CommandEmpty>
                <CommandGroup :heading="t('Sugestões')" v-if="inputValue.length === 0">
                    <CommandItem v-for="data in nfts.slice(0, 2)" :key="data.id" :value="data.name"
                        :to="`/nfts/${data.id}`" @click="saveToLocalStorage(data)">
                        <span class="text-light">{{ data.name }}</span>
                        <span class="ml-1 text-dark text-xs">({{ data.symbol }})</span>
                    </CommandItem>
                </CommandGroup>
                <CommandGroup :heading="t('Recomendado')" v-if="inputValue.length > 0">
                    <CommandItem v-for="data in searchResponse" :value="data.name" :to="`/nfts/${data.id}`"
                        @click="saveToLocalStorage(data)" :key="data.id">
                        <span class="text-light">{{ data.name }}</span>
                        <span class="ml-1 text-dark text-xs">({{ data.symbol }})</span>
                    </CommandItem>
                </CommandGroup>
            </CommandList>
        </CommandDialog>
    </Command>
</template>
